<template>
    <vx-table-row>
        <vx-table-row-title>
            <div class="flex items-center gap-3">
                <div class="cursor-pointer" data-test="toggle-favorite" @click="toggleFavorite()">
                    <font-awesome-icon
                        widthAuto
                        v-if="team.is_favorite"
                        :icon="faStar"
                        class="text-amber-400"
                    ></font-awesome-icon>
                    <font-awesome-icon widthAuto v-else :icon="faStarReg" class="text-slate-500"></font-awesome-icon>
                </div>
                <div>
                    <vx-badge color="slate" size="xs">
                        {{ team.id }}
                    </vx-badge>
                    {{ team.name }}
                </div>
            </div>
        </vx-table-row-title>

        <vx-table-row-item data-test="status">
            <template v-slot:label> Status: </template>
            <vx-popover hover placement="top" arrow>
                <span>{{ formatTeamStatus(team.status) }}</span>
                <template v-if="team.status_reason" v-slot:content>
                    <div class="max-w-xs">
                        {{ team.status_reason }}
                    </div>
                </template>
            </vx-popover>
        </vx-table-row-item>
        <vx-table-row-item data-test="parent">
            <template v-slot:label> Parent Team: </template>
            <div
                v-if="team.parent_id"
                class="text-sm font-bold line-clamp-2"
                :title="`${team.parent_id} - ${team.parent_name}`"
            >
                <vx-badge color="slate" size="xs">{{ team.parent_id }}</vx-badge> {{ team.parent_name }}
            </div>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-dropdown placement="right">
                <vx-button
                    data-test="dropdown"
                    aria-label="Team actions"
                    size="sm"
                    type="button"
                    color="muted"
                    outline
                    class="w-9"
                >
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <slot name="actionItems"></slot>

                    <vx-dropdown-item :href="`/kiosk/teams/${team.id}`">
                        View
                        <font-awesome-icon widthAuto :icon="faEye"></font-awesome-icon>
                    </vx-dropdown-item>

                    <vx-dropdown-item v-if="team.is_favorite" @click="toggleFavorite()">
                        Remove Favorite
                        <font-awesome-icon widthAuto :icon="faStar"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item v-else @click="toggleFavorite()">
                        Favorite
                        <font-awesome-icon widthAuto :icon="faStarReg"></font-awesome-icon>
                    </vx-dropdown-item>

                    <vx-dropdown-item v-if="team.status === 'active'" @click="handleSuspend" data-test="suspend">
                        Suspend
                        <font-awesome-icon widthAuto :icon="faPause"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item v-else-if="team.status !== 'closed'" @click="handleActivate" data-test="activate">
                        {{ team.status === 'suspended' ? 'Re-Activate' : 'Activate' }}
                        <font-awesome-icon widthAuto :icon="faPlay"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button
                :href="`/kiosk/users/${team.owner_id}/impersonate?team_id=${team.id}`"
                :disabled="team.owner_id === currentUserId || team.status === 'closed' || props.disableImpersonation"
                size="sm"
                data-test="impersonate"
                outline
                color="muted"
            >
                Impersonate <font-awesome-icon widthAuto :icon="faMasksTheater" />
            </vx-button>
        </vx-table-row-buttons>

        <kiosk-team-billing-review-modal
            v-model:visible="billingReviewModal"
            v-model:team="team"
            :action="billingReviewAction"
        ></kiosk-team-billing-review-modal>
    </vx-table-row>
</template>

<script setup>
import { faEllipsis, faEye, faPause, faPlay, faStar, faMasksTheater } from '@fortawesome/pro-solid-svg-icons';
import { faStar as faStarReg } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxPopover,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { adminTeamsClient } from '~/services';
import { formatTeamStatus } from '~/utils/team';
import { ref } from 'vue';
import KioskTeamBillingReviewModal from '~/components/kiosk/kiosk-teams/modals/KioskTeamBillingReviewModal.vue';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';

const props = defineProps({
    disableImpersonation: {
        type: Boolean,
        default: false,
    },
});

const team = defineModel({
    required: true,
    type: Object,
});

const currentUserId = Spark.state.user.id;

const billingReviewModal = ref(false);
const billingReviewAction = ref('');

const handleActivate = () => {
    billingReviewAction.value = 'activate';
    billingReviewModal.value = true;
};

const handleSuspend = () => {
    billingReviewAction.value = 'suspend';
    billingReviewModal.value = true;
};

const toasted = useToasted();

const togglingFavorite = ref(false);
const toggleFavorite = async () => {
    if (togglingFavorite.value) {
        return;
    }
    togglingFavorite.value = true;
    try {
        if (team.value.is_favorite) {
            await adminTeamsClient.deleteFavoriteTeam(team.value.id);
            team.value.is_favorite = false;
            toasted.success(`${team.value.id} - ${team.value.name} has been removed from your favorite teams.`);
        } else {
            await adminTeamsClient.favoriteTeam(team.value.id);
            team.value.is_favorite = true;
            toasted.success(`${team.value.id} - ${team.value.name} has been added to your favorite teams.`);
        }
    } catch (e) {
        console.error(e);
        if (e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }

        toasted.error(e.response?.data?.message || 'Something went wrong.');
    } finally {
        togglingFavorite.value = false;
    }
};
</script>
